import React, { useContext, useEffect, useState } from "react";

import { ZeosButton } from "../../../components/Shared/ZeosButton";
import { ZeosInput } from "../../../components/Shared/ZeosInput";
import { AuthContext } from "../../../context/AuthContext";
import { ZeosApiContext } from "../../../context/ZeosApiContext";
import { useFormValidation } from "../../../hooks/useFormValidation";
import { IZeosUser } from "../../../services/auth/IZeosUser";
import {
  handleZeosEnviromentEmailWhitelist,
  isValidEmail,
  isDemo,
  ZeosLocalModeWarning,
  ZeosQaModeWarning,
} from "../../../services/environment/EnvironmentHelpers";
import { accountVerificationSchema } from "../../../services/forms/validationSchemas/accountVerificationSchema";
import { maskEmail } from "../../../services/helpers/StringHelpers";
import { IZeosUserConfirmation } from "../../../services/zeos-api/contracts/Account";
import { REQUEST_ERROR } from "../../../services/zeos-api/ZeosApiExecutor";
import { OnboardContainer } from "../layout/OnboardContainer";

const defaultForm = {
  code: "",
};

export const AccountVerificationScreen = () => {
  const authContext = useContext(AuthContext);
  const dataContext = useContext(ZeosApiContext);

  const [isError, setIsError] = useState(false);
  const [newZeosUser, setNewZeosUser] = useState<IZeosUser>();

  // Set when the page loads with no pending identity -- after a refresh, a closed
  // tab, or the link in the verification email opened later on. The code form is
  // useless then, so the screen asks for the address instead of rendering a dead
  // field and reporting a code the server never got a chance to check as invalid.
  const [needsResume, setNeedsResume] = useState(false);
  const [resumeEmail, setResumeEmail] = useState("");
  const [resumeError, setResumeError] = useState("");
  const [resumeMessage, setResumeMessage] = useState("");
  const [isResuming, setIsResuming] = useState(false);

  const [emailDraft, setEmailDraft] = useState("");
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [resendMessage, setResendMessage] = useState("");
  const [resendError, setResendError] = useState("");
  const [isResending, setIsResending] = useState(false);

  const { formData, errors, formValidate, setFormData } = useFormValidation<
    typeof defaultForm
  >(accountVerificationSchema, defaultForm);

  useEffect(() => {
    const initAsync = async () => {
      const user = authContext.getNewZeosUser();
      if (!user) {
        setNeedsResume(true);
        return;
      }
      setNewZeosUser(user);
      setEmailDraft(user.emailAddress || "");
    };
    initAsync();
  }, []);

  // Countdown for the resend cooldown; the server's response is the source of
  // truth for the remaining seconds, this just ticks the UI down between calls.
  useEffect(() => {
    if (cooldownSeconds <= 0) {
      return;
    }
    const timer = setTimeout(() => setCooldownSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldownSeconds]);

  const handleResend = async (emailOverride?: string) => {
    if (!newZeosUser?.zeosUserId || isResending) {
      return;
    }
    setIsResending(true);
    setResendError("");
    setResendMessage("");
    try {
      const response = await dataContext.Account.resendVerificationCodeAsync({
        zeosUserId: newZeosUser.zeosUserId,
        emailAddress: emailOverride ?? emailDraft,
      });

      if (!response.data) {
        setResendError(
          response.message === REQUEST_ERROR
            ? "Failed to resend code. Please try again."
            : response.message
        );
        return;
      }

      setCooldownSeconds(response.data.cooldownSecondsRemaining);
      if (response.data.codeSent) {
        setResendMessage("A new code was sent.");
        setNewZeosUser((prev) =>
          prev ? { ...prev, emailAddress: response.data.emailAddress } : prev
        );
        authContext.setNewZeosUser({
          ...newZeosUser,
          emailAddress: response.data.emailAddress,
        });
      }
    } finally {
      setIsResending(false);
    }
  };

  /**
   * Recovery path for a visitor with no pending identity: the address is all they
   * can offer. The API answers identically whether or not it is on file, so this
   * never confirms an account exists.
   */
  const handleResume = async () => {
    if (isResuming) {
      return;
    }
    const checkEmailDomain = handleZeosEnviromentEmailWhitelist(resumeEmail);
    if (!isValidEmail(resumeEmail) || !checkEmailDomain.success) {
      setResumeError(
        !isValidEmail(resumeEmail)
          ? "Please enter a valid email address."
          : checkEmailDomain.message
      );
      return;
    }
    setIsResuming(true);
    setResumeError("");
    try {
      const response = await dataContext.Account.resumeVerificationCodeAsync({
        emailAddress: resumeEmail,
      });

      if (!response.data) {
        setResumeError(
          response.message === REQUEST_ERROR
            ? "Failed to send a new code. Please try again."
            : response.message
        );
        return;
      }

      setEmailDraft(resumeEmail);
      setCooldownSeconds(response.data.cooldownSecondsRemaining);
      setResumeMessage(
        "If that address has a signup waiting, a new code is on its way. Enter it below."
      );
      setNeedsResume(false);
    } finally {
      setIsResuming(false);
    }
  };

  const handleStartEditEmail = () => {
    // Without an identity there is nothing to re-address; the resume prompt is
    // the only thing that can put this screen back on a real account.
    if (!newZeosUser) {
      setResumeMessage("");
      setResumeError("");
      setNeedsResume(true);
      return;
    }
    setEmailError("");
    setIsEditingEmail(true);
  };

  const handleSaveEmail = async () => {
    const checkEmailDomain = handleZeosEnviromentEmailWhitelist(emailDraft);
    if (!isValidEmail(emailDraft) || !checkEmailDomain.success) {
      setEmailError(
        !isValidEmail(emailDraft)
          ? "Please enter a valid email address."
          : checkEmailDomain.message
      );
      return;
    }
    setEmailError("");
    setIsEditingEmail(false);
    await handleResend(emailDraft);
  };

  useEffect(() => {
    if (!isDemo()) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      code: prev.code || "123456",
    }));
  }, [setFormData]);

  const handleChange = (code: string) => {
    setFormData({
      ...formData,
      code: code.trim(),
    });
  };

  const handleNext = async (doNavigate: () => void) => {
    setIsError(false);
    formValidate(async () => await validationCallback(doNavigate));
  };

  const validationCallback = async (doNavigate: () => void) => {
    const code = formData.code;
    const verification: IZeosUserConfirmation = {
      zeosUserId: newZeosUser?.zeosUserId || 0,
      accessCode: code,
      // After a resume there is no id to send, only the address the code went to.
      emailAddress: newZeosUser?.emailAddress || emailDraft,
      password: "",
    };

    const verificationResponse =
      await dataContext.Account.postAccountVerificationAsync(verification);

    //TODO: This is bookmark for needing to manage request errors throughout the website
    if (verificationResponse.message == REQUEST_ERROR) {
      console.error("API ERROR");
    }

    const confirmation = verificationResponse.data;
    // The server checks the code now, so a client-side override would only carry the
    // screen past this step and strand the user at the password call, which checks it
    // too. Use the code the account was actually sent.
    const verified = confirmation?.verified === true;

    if (!verified) {
      setIsError(true);
      return;
    }

    // The resume path carries no user id, so take the one the confirmation hands
    // back before falling back to whatever identity we already had.
    const confirmedUserId =
      confirmation?.zeosUserId || newZeosUser?.zeosUserId || 0;

    if (!confirmedUserId) {
      // Verified, but nothing identifies the account for the password step that
      // follows. "Invalid code" would be the wrong story to tell here.
      setIsError(false);
      setResumeMessage("");
      setResumeError(
        "We lost track of your signup. Enter your email and we’ll send a fresh code."
      );
      setNeedsResume(true);
      return;
    }

    const firstName = confirmation?.firstName || newZeosUser?.firstName || "";
    const lastName = confirmation?.lastName || newZeosUser?.lastName || "";
    const confirmedUser: IZeosUser = {
      displayName: `${firstName} ${lastName}`.trim(),
      zeosUserId: confirmedUserId,
      principalId: newZeosUser?.principalId ?? 0,
      authSystemId: newZeosUser?.authSystemId ?? "",
      firstName,
      lastName,
      emailAddress: confirmation?.emailAddress || verification.emailAddress,
      phone: newZeosUser?.phone ?? "",
      customerClaims: newZeosUser?.customerClaims ?? [],
      summaryEmailSent: newZeosUser?.summaryEmailSent ?? false,
      isSuperAdmin: newZeosUser?.isSuperAdmin ?? false,
    };

    setNewZeosUser(confirmedUser);
    authContext.setNewZeosUser(confirmedUser);
    authContext.setVerificationCode(code);
    // The stored copy exists to get the user back to this screen; past it, leaving
    // it behind would let a later visit try to resume an already-verified account.
    authContext.clearPendingSignupStorage();
    setIsError(false);
    doNavigate();
  };

  const form = (
    <>
      <div className="col-span-12 sm:col-span-8 md:col-span-6 space-y-6">
        {/* <ZeosLocalModeWarning />
        <ZeosQaModeWarning /> */}
        <p className="text-base text-zeos-black">
          We’ve sent a 6-digit verification code to your email address. Please
          enter the code below to verify your account and continue onboarding.
        </p>

        {resumeMessage && (
          <p className="text-sm text-green-700">{resumeMessage}</p>
        )}

        {isEditingEmail ? (
          <div className="space-y-2">
            <ZeosInput
              name="verificationEmail"
              label="Email Address"
              placeholder="Enter your email"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              error={emailError}
              required={true}
            />
            <div className="flex gap-3">
              <ZeosButton
                label="Save & resend code"
                type="button"
                mode="action"
                onPress={handleSaveEmail}
                isDisabled={isResending}
              />
              <ZeosButton
                label="Cancel"
                type="button"
                mode="prev"
                onPress={() => {
                  setEmailDraft(newZeosUser?.emailAddress || "");
                  setEmailError("");
                  setIsEditingEmail(false);
                }}
              />
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-600">
            We sent a code to {maskEmail(emailDraft)}.{" "}
            <a
              className="text-zeos-orange-dk cursor-pointer"
              onClick={handleStartEditEmail}
            >
              Not your email? Edit
            </a>
          </p>
        )}

        <div className="w-1/2">
          <ZeosInput
            name="verificationCode"
            label="Verification Code"
            placeholder="Enter your 6-digit code"
            value={formData.code}
            onChange={(e) => handleChange(e.target.value)}
            error={
              errors.code || (isError ? "Invalid code. Please try again." : "")
            }
            required={true}
          />
        </div>

        <div className="space-y-2">
          <ZeosButton
            label={
              cooldownSeconds > 0
                ? `Resend code (${cooldownSeconds}s)`
                : "Resend code"
            }
            type="button"
            mode="action"
            onPress={() => (newZeosUser ? handleResend() : handleResume())}
            isDisabled={
              isResending ||
              isResuming ||
              cooldownSeconds > 0 ||
              isEditingEmail
            }
          />
          {resendMessage && (
            <p className="text-sm text-green-700">{resendMessage}</p>
          )}
          {resendError && (
            <p className="text-sm text-red-600">{resendError}</p>
          )}
        </div>
      </div>
    </>
  );

  const resumeForm = (
    <div className="col-span-12 sm:col-span-8 md:col-span-6 space-y-6">
      <p className="text-base text-zeos-black">
        We don’t have your signup details on this device any more. Enter the
        email address you signed up with and we’ll send a fresh verification
        code.
      </p>

      <div className="w-full sm:w-2/3">
        <ZeosInput
          name="resumeEmail"
          label="Email Address"
          placeholder="Enter your email"
          value={resumeEmail}
          onChange={(e) => setResumeEmail(e.target.value)}
          error={resumeError}
          required={true}
        />
      </div>

      <ZeosButton
        label="Send me a code"
        type="button"
        mode="action"
        onPress={handleResume}
        isDisabled={isResuming}
      />
    </div>
  );

  return (
    <OnboardContainer
      content={needsResume ? resumeForm : form}
      contentType="form"
      pageSlug="account-verification"
      isContinueDisabled={needsResume}
      onNext={handleNext}
    />
  );
};
